<template>
	<app-table actions-col-name="Status" :columns="columns" :data="orders">
		<template v-slot:orderID="{ item }">
			<div class="flex flex-row items-center gap-1">
				<CountdownTimer
					v-if="
						item['enableCookingTime'] &&
							rerenderKey &&
							!item.b2bOrder &&
							(item['status'] === statuses.pending ||
								item['status'] === statuses.accepted ||
								item['status'] === statuses.pickedUp)
					"
					:acceptedTime="
						item['status'] === statuses.pending ? item['lastDatePending'] : item['lastDateAccepted']
					"
					:readyTime="
						item['status'] === statuses.pending ? posSettings.maxPendingMinutes : item.totalCookingTime
					"
				/>
				<app-details-shortcut :routeName="RouteNames.OrderDetails" :identifier="encrypt(item['orderID'])">
					<span class="font-bold text-2xl">#{{ formatOrderId(item['orderID']) }}</span>
				</app-details-shortcut>
				<div
					@click="onSetPackage(item)"
					class="flex items-center justify-center w-8 h-8 rounded-lg bg-background font-semibold cursor-pointer mr-1"
				>
					{{ item.packagesNo || 0 }}
				</div>
				<app-icon class="text-info cursor-pointer text-2xl" icon="info" @click="onNotesClick(item)" />
				<img v-if="item.b2bOrder" src="@/assets/images/b2b_logo.png" alt="b2b" class="w-4 h-4" />
				<app-tooltip v-if="item.errorMsg" :text="item.errorMsg">
					<app-icon icon="attention" class="text-primary animate-ping mr-1 cursor-pointer" />
				</app-tooltip>
				<app-tooltip v-if="item.orderExtraData?.cashless" text="Cashless" class="w-5">
					<img src="../../assets/images/cashless.png" alt="cashless" class="w-5 h-5" />
				</app-tooltip>
			</div>
		</template>

		<template v-slot:type="{ item }">
			<order-type-section :item="item" @onNotesClick="onNotesClick" />
		</template>
		<template v-slot:registrationtime="{ item }">
			<span class="text-18">{{ $filters.formatDateNoSeconds(item.registrationtime) }}</span>
		</template>
		<template v-slot:customer="{ item }">
			<app-details-shortcut :route-name="RouteNames.CustomerDetails" :identifier="item['customer'].phone">
				<span class="cursor-pointer text-info">{{ item['customer'].phone }}</span>
			</app-details-shortcut>
		</template>
		<template v-slot:restaurantName="{ item }">
			<span class="text-18">{{ item['restaurant'].restaurantName }}</span>
		</template>
		<template v-slot:total="{ item }">
			<div class="flex flex-row items-center justify-end mr-4 gap-1 text-18">
				<span
					class="bg-primary text-white text-10 p-1 rounded-md"
					v-if="item['discount'] && item['discount']['discount'] > 0"
				>
					-{{ item['discount']['discount'] }}
				</span>
				<app-tooltip v-if="item.thirdParty && item.thirdParty.syncFiscalizationUrl" text="Fiscal invoice">
					<a :href="item.thirdParty.syncFiscalizationUrl" target="_blank" class="text-info cursor-pointer">
						<app-icon icon="doc" class="text-tiny text-info hover:text-primary" />
					</a>
				</app-tooltip>
				<span> {{ $filters.formatCurrency(item.total, item.currency) }}</span>
			</div>
		</template>
		<template v-slot:paymentType="{ item }">
			<div
				v-if="item['paymentMethod'].paymentType !== 'CASH'"
				class="flex gap-2 justify-start place-items-center"
			>
				<app-tooltip text="Credit Card" v-if="item['paymentMethod'].paymentType === 'CREDIT_CARD'">
					<img alt="credit-card" class="w-6 h-auto" src="../../assets/images/credit-card.svg" />
				</app-tooltip>
				<span v-else>{{ $filters.capitalization(item['paymentMethod'].paymentType) }}</span>
				<span class="w-3 h-3 ml-2 bg-success rounded-full"></span>
			</div>
		</template>
		<template v-slot:date="{ item }">
			<div class="flex items-center gap-1">
				<span> {{ $filters.formatDate(item.date) }} </span>
			</div>
		</template>
		<template v-slot:address="{ item }">
			<app-tooltip :text="item.address">
				<div class="w-16 lg:w-24 xl:w-48 truncate">
					<span class="text-18">{{ item.address }}</span>
				</div>
			</app-tooltip>
		</template>
		<template v-slot:status="{ item }">
			<div class="flex items-center justify-between">
				<span class="text-18">{{
					$t(orderStatus(item.status, item.isTakeAwayOrder, item.enableCookingTime))
				}}</span>
				<div class="flex flex-col gap-1">
					<order-status-icon
						:status="item['status']"
						:is-takeaway="item.isTakeAwayOrder"
						:enable-cooking-time-prop="item['enableCookingTime']"
						size="sm"
						:loading="loadingActions[item['orderID']]"
						@answer="answerOrder(item)"
						@cookOrder="cookOrder(item)"
						@deliverOrder="deliverOrder(item)"
					/>
					<order-status-progress-bar :order-status="item['status']" />
				</div>
			</div>
		</template>
	</app-table>
	<app-modal
		v-if="selectedOrder"
		@close="selectedOrder = null"
		:title="`Order #${formatOrderId(selectedOrder['orderID'])}`"
	>
		<div class="flex flex-col w-64 p-2">
			<span class="font-bold">Address:</span>
			<span class="border-b-default border-border-color mb-2">{{ selectedOrder.address }}</span>
			<span class="font-bold">Address details:</span>
			<span class="border-b-default border-border-color mb-2">
				{{ selectedOrder['addressDetails'] || 'No address details found' }}
			</span>
			<span class="font-bold">Order details:</span>
			<span class="border-b-default border-border-color mb-2">
				{{ selectedOrder.description || 'No order details found' }}
			</span>
		</div>
	</app-modal>
	<select-packages-modal
		v-if="isSelectingPackageModal"
		@close="isSelectingPackageModal = false"
		@selected="onPackageSelected"
		:order-id="selectedOrderId"
	/>
</template>

<script>
	import AppModal from '@/components/app/AppModal';
	import AppTable from '@/components/app/AppTable';
	import constants from '@/constants/orders';
	import AppDetailsShortcut from '@/components/app/AppDetailsShortcut';
	import RouteNames from '@/router/names';
	import AppTooltip from '@/components/app/AppTooltip';
	import mapOrderStatuses from '@/constants/mapOrderStatuses';
	import { statuses } from '@/constants/mapOrderStatuses';
	import SelectPackagesModal from '@/components/orders/SelectPackagesModal';
	import OrderService from '../../services/OrderService';
	import OrderTypeSection from '@/components/orders/OrderTypeSection';
	import UtilityService from '@/services/UtilityService';
	import OrderStatusIcon from '@/components/orders/OrderStatusAction';
	import orders from '@/constants/orders';
	import OrderStatusProgressBar from '@/components/orders/OrderStatusProgressBar';
	import AppIcon from '@/components/app/AppIcon.vue';
	import CountdownTimer from '@/components/orders/CountdownTimer.vue';
	import { mapGetters } from 'vuex';

	export default {
		name: 'OrdersTable',
		components: {
			CountdownTimer,
			AppIcon,
			OrderStatusProgressBar,
			OrderStatusIcon,
			OrderTypeSection,
			AppTooltip,
			AppTable,
			AppModal,
			AppDetailsShortcut,
			SelectPackagesModal,
		},
		emits: ['onOrderUpdate', 'onStatusChange', 'updateLocalOrder'],
		props: {
			orders: {
				required: true,
				type: Array,
			},
			disabled: {
				required: false,
				type: Boolean,
			},
			loaders: {
				required: false,
				default: {},
			},
		},
		data: () => ({
			RouteNames,
			columns: constants.columns,
			orderStatuses: mapOrderStatuses,
			statuses,
			selectedOrder: null,
			showCouriersModal: false,
			removeCourierModal: false,
			selectedOrderForAssignment: null,
			loadingActions: {},
			removeReasons: [],
			removeReason: {
				id: 11,
				notes: '',
			},
			selectedCourier: {},
			selectedOrderId: null,
			isSelectingPackageModal: false,
			rerenderKey: true,
		}),
		computed: {
			canSubmit() {
				return this.removeReason.id !== 0 || this.removeReason.notes.length;
			},
			...mapGetters({
				posSettings: 'posSettings',
			}),
		},
		methods: {
			orderStatus(status, isTakeaway, enableCookingTime) {
				let statuses = enableCookingTime ? 'statusForCookingTime' : 'status';
				return isTakeaway
					? 'takeaway'
					: orders[statuses].find((_status) => _status.propValue === status)?.title;
			},
			formatOrderId(id) {
				return OrderService.formatOrderId(id);
			},
			encrypt(id) {
				return UtilityService.encrypt(id);
			},
			onNotesClick(order) {
				this.emitter.emit('onNewOrderReceived', { orderId: order.orderID, isDumb: true });
			},
			onSetPackage(item) {
				this.selectedOrderId = item.orderID;
				this.isSelectingPackageModal = true;
			},
			onPackageSelected(packagesNo) {
				this.$emit('updateLocalOrder', {
					orderId: this.selectedOrderId,
					props: {
						packagesNo,
					},
				});
				this.isSelectingPackageModal = false;
			},
			answerOrder(item) {
				this.emitter.emit('onNewOrderReceived', { orderId: item.orderID });
			},
			deliverOrder(item) {
				console.log(item);
			},
			cookOrder({ orderID }) {
				this.loadingActions[orderID] = true;
				OrderService.cookOrder(orderID)
					.then(() => {
						this.$emit('updateLocalOrder', {
							orderId: orderID,
							props: {
								status: 'COOKED',
							},
						});
						this.selectedOrderId = orderID;
						this.isSelectingPackageModal = true;
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.loadingActions[orderID] = false;
					});
			},
			handleRerenderTimer() {
				this.rerenderKey = false;
				this.$nextTick(() => {
					this.rerenderKey = true;
				});
			},
		},
		mounted() {
			this.emitter.on('rerenderTimer', () => {
				this.handleRerenderTimer();
			});
		},
		beforeUnmount() {
			this.emitter.off('rerenderTimer', null);
		},
	};
</script>

<style lang="scss" scoped>
	:deep(.btn.ladda-button.small) {
		padding: 0 !important;
		min-width: 0 !important;
		font-size: 13px;
	}
</style>
